import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './TutorDashboard.css';

const TutorDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [requestGroups, setRequestGroups] = useState([]);
  const [subjectPresence, setSubjectPresence] = useState({});
  const [sessions, setSessions] = useState([]);
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [walletBalance, setWalletBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);

  // Modal states
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showNewSessionModal, setShowNewSessionModal] = useState(false);

  // New session form
  const [sessionForm, setSessionForm] = useState({
    subject: '', topic: '', description: '', startDate: '', endDate: '', link: ''
  });

  // ── View Details reveal state ──
  const [revealedSessionId, setRevealedSessionId] = useState(null);
  const revealTimeoutRef = useRef(null);

  const walletGraphRef = useRef(null);
  const badgeRef = useRef(null);

  const API_BASE = 'https://mykaddle-api.onrender.com';

  // ── API Calls ──
  const fetchTutorSubjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE}/api/tutor/subjects/dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSubjects(res.data || []);
    } catch (err) {
      console.error('Failed to load subjects', err);
    }
  }, []);

  const fetchTutorRequestGroups = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE}/api/tutor/requests/history?type=tutor`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const requests = res.data.requests || [];
      const activeGroups = requests
        .filter(r => r.status === 'accepted' && !r.isFinished && r.groupSubjectId)
        .map(r => ({
          subjectId: r.groupSubjectId,
          subjectDisplayName: r.subjectDisplayName,
          isRequestBased: true,
        }));
      setRequestGroups(activeGroups);
    } catch (err) {
      console.error('Failed to load request-based groups', err);
      setRequestGroups([]);
    }
  }, []);

  const fetchSessions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/api/tutor/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(res.data || []);
    } catch (err) {
      console.error('Failed to fetch sessions', err);
      setSessions([]);
    }
  }, []);

  // ── Fetch notifications ──
  const fetchNotifications = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_BASE}/api/notifications?limit=50`, { headers });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    }
  }, []);

  //--data for graph
  const fetchWalletData = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    const [walletRes, txRes] = await Promise.all([
      axios.get(`${API_BASE}/api/payments/wallet`, { headers }),
      axios.get(`${API_BASE}/api/payments/tutor/transactions`, { headers })
    ]);

    setWalletBalance(walletRes.data?.available_amount || 0);
    setTransactions(txRes.data || []);
  } catch (err) {
    console.error('Failed to load wallet data', err);
  }
}, []);

  // ── Fetch unread count (lightweight poll) ──
  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API_BASE}/api/notifications/unread-count`, { headers });
      setUnreadCount(res.data.unreadCount || 0);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (!token || !storedUser) { navigate('/auth'); return; }
    setUser(JSON.parse(storedUser));
    fetchTutorSubjects();
    fetchSessions();
    fetchTutorRequestGroups();
    fetchUnreadCount();
    fetchWalletData();
  }, [navigate, fetchTutorSubjects, fetchSessions, fetchTutorRequestGroups, fetchUnreadCount]);

  // Poll unread count every 30s
  useEffect(() => {
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Socket.io presence
  useEffect(() => {
    const socket = io('https://mykaddle-api.onrender.com');
    socket.on('subjectPresenceUpdate', ({ subjectId, count }) => {
      setSubjectPresence(prev => ({ ...prev, [subjectId]: count }));
    });
    return () => socket.disconnect();
  }, []);

  // ── Navigation handlers ──
  const goToProfile = () => navigate('/tutor/profile/edit');
  const goToSubjects = () => {
    navigate('/TeachingCentre/AddSubjects');
  }
  const goToSupport = () => navigate('/dashboard/support');
  const goToWallet = () => navigate('/dashboard/TutorWallet');
  const goToRequests = () => navigate('/tutor/tutorReq');

  // ── Logout handlers ──
  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleLogoutCancel = () => setShowLogoutModal(false);
  const handleLogoutConfirm = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/auth');
  };

  // ── Date chip helpers ──
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();

  const getDateChips = () => {
    const chips = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const dayLabel = days[d.getDay()];
      const dayNum = d.getDate();
      chips.push({ key: `${dayLabel} ${dayNum}`, label: dayLabel, num: dayNum, isToday: i === 0 });
    }
    return chips;
  };

  useEffect(() => {
    if (!selectedDateKey) {
      const chips = getDateChips();
      if (chips.length > 0) setSelectedDateKey(chips[0].key);
    }
  }, []);

  // ── Sessions for selected date ──
  const getSessionsForDate = (dateKey) => {
    if (!dateKey) return [];
    return sessions.filter(s => {
      const start = new Date(s.start_time);
      const dayLabel = days[start.getDay()];
      return `${dayLabel} ${start.getDate()}` === dateKey;
    });
  };

  const formatRemainingTime = (start, end) => {
    const now = new Date();
    const startTime = new Date(start);
    const endTime = new Date(end);
    if (now >= endTime) return null;
    if (now >= startTime) return "Started";
    let diff = startTime - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    diff -= days * (1000 * 60 * 60 * 24);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (days > 0) return `${days}d ${hours}h remaining`;
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  // ── View Details handler ──
  const handleViewDetails = (session, index) => {
    const id = session._id || index;
    if (revealTimeoutRef.current) clearTimeout(revealTimeoutRef.current);
    setRevealedSessionId(id);
    revealTimeoutRef.current = setTimeout(() => {
      setRevealedSessionId(null);
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (revealTimeoutRef.current) clearTimeout(revealTimeoutRef.current);
    };
  }, []);

  // ── New session form ──
  const openNewSessionModal = () => {
    setSessionForm({ subject: '', topic: '', description: '', startDate: '', endDate: '', link: '' });
    setShowNewSessionModal(true);
  };

  const closeNewSessionModal = () => setShowNewSessionModal(false);

  const handleSessionFormChange = (e) => {
    const { id, value } = e.target;
    setSessionForm(prev => ({ ...prev, [id]: value }));
  };

  const createSession = async (e) => {
    e.preventDefault();
    const { subject, topic, description, startDate, endDate, link } = sessionForm;
    if (!subject) { alert('Please select a subject'); return; }
    if (!topic) { alert('Please enter a topic'); return; }
    if (!startDate) { alert('Please select a start date and time'); return; }
    if (!endDate) { alert('Please select an end date and time'); return; }
    if (new Date(endDate) <= new Date(startDate)) {
      alert('End time must be after start time');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const selectedSubjectObj = sessionSubjectOptions.find(sub => sub.subjectId === subject);
      const tutorId = user?._id || user?.id;
      await axios.post(
        `${API_BASE}/api/tutor/create-session`,
        {
          tutor_id: tutorId,
          tutor_name: `${user?.firstName} ${user?.lastName}`,
          tutee_id: 'pending',
          tutee_name: 'Pending Assignment',
          subject_id: subject,
          subject_name: selectedSubjectObj?.subjectDisplayName || '',
          topic,
          description,
          class_link: link,
          start_time: startDate,
          end_time: endDate,
          status: 'scheduled'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Session created successfully!');
      fetchSessions();
      closeNewSessionModal();
    } catch (err) {
      console.error("Session Error:", err.response?.data || err.message);
      alert(err.response?.data?.message || 'Failed to create session');
    }
  };

  // ── Time-based greeting ──
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // ── Wallet graph (placeholder zeros) ──
  const drawGraph = useCallback(() => {
    const wrap = walletGraphRef.current;
    const badge = badgeRef.current;
    if (!wrap || !badge) return;

    const canvas = wrap.querySelector('canvas');
    if (!canvas) return;

    const W = wrap.clientWidth;
    const H = wrap.clientHeight;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, W, H);

    const balances = transactions.length > 0
      ? (() => {
          const pts = [0];
          let running = 0;
          for (const t of transactions) {
            running += t.type === 'credit' ? t.amount : -t.amount;
            pts.push(running);
          }
          return pts;
        })()
      : [0, 0, 0, 0, 0];

    const minVal = Math.min(...balances);
    const maxVal = Math.max(...balances) || 1;
    const range = maxVal - minVal || 1;
    const padX = 8, padTop = 14, padBot = 10;
    const drawW = W - padX * 2;
    const drawH = H - padTop - padBot;

    const toX = (i) => padX + (i / (balances.length - 1)) * drawW;
    const toY = (v) => padTop + drawH - ((v - minVal) / range) * drawH;

    const smoothPath = () => {
      ctx.beginPath();
      ctx.moveTo(toX(0), toY(balances[0]));
      for (let i = 0; i < balances.length - 1; i++) {
        const x0 = toX(i), y0 = toY(balances[i]);
        const x1 = toX(i + 1), y1 = toY(balances[i + 1]);
        const cpx = (x0 + x1) / 2;
        ctx.bezierCurveTo(cpx, y0, cpx, y1, x1, y1);
      }
    };

    const grad = ctx.createLinearGradient(0, padTop, 0, H);
    grad.addColorStop(0, 'rgba(155, 93, 229, 0.30)');
    grad.addColorStop(1, 'rgba(155, 93, 229, 0.00)');

    smoothPath();
    ctx.lineTo(toX(balances.length - 1), H);
    ctx.lineTo(toX(0), H);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    smoothPath();
    ctx.strokeStyle = '#9b5de5';
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.stroke();

    const lastX = toX(balances.length - 1);
    const lastY = toY(balances[balances.length - 1]);
    badge.style.left = lastX + 'px';
    badge.style.top = lastY + 'px';
  }, [transactions]);

  useEffect(() => {
    if (showNewSessionModal) return;
    const timer = setTimeout(drawGraph, 100);
    window.addEventListener('resize', drawGraph);
    return () => window.removeEventListener('resize', drawGraph);
  }, [drawGraph, showNewSessionModal]);

  // ── Open notification modal & fetch full list ──
  const handleBellClick = () => {
    fetchNotifications();
    setShowNotifModal(true);
  };

  // ── Mark single notification as read ──
  const handleNotificationClick = async (notif) => {
    if (notif.read) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_BASE}/api/notifications/${notif.id}/read`, {}, { headers });
      
      setNotifications(prev =>
        prev.map(n => n.id === notif.id ? { ...n, read: true, readAt: new Date().toISOString() } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));

      if (notif.actionUrl) {
        setShowNotifModal(false);
        navigate(notif.actionUrl);
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const currentSessions = getSessionsForDate(selectedDateKey);
  const dateChips = getDateChips();

  const sessionSubjectOptions = [...subjects, ...requestGroups];

  if (!user) return <div className="td-loading">Loading...</div>;

  return (
    <div className="td-dashboard-container">
      <div className="td-almighty-wrapper">

        {/* ── Sessions Panel (Left Sidebar) ── */}
        <aside className="td-sessions-panel">
          <div className="td-sessions-header">
            <span className="td-sessions-title">
              <svg className="td-sessions-title-icon" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 256 256">
                <path d="M0 0h256v256H0z" fill="none" />
                <path fill="currentColor" d="M208 36h-28V24a4 4 0 0 0-8 0v12H84V24a4 4 0 0 0-8 0v12H48a12 12 0 0 0-12 12v160a12 12 0 0 0 12 12h160a12 12 0 0 0 12-12V48a12 12 0 0 0-12-12M48 44h28v12a4 4 0 0 0 8 0V44h88v12a4 4 0 0 0 8 0V44h28a4 4 0 0 1 4 4v36H44V48a4 4 0 0 1 4-4m160 168H48a4 4 0 0 1-4-4V92h168v116a4 4 0 0 1-4 4m-72-80a8 8 0 1 1-8-8a8 8 0 0 1 8 8m44 0a8 8 0 1 1-8-8a8 8 0 0 1 8 8m-88 40a8 8 0 1 1-8-8a8 8 0 0 1 8 8m44 0a8 8 0 1 1-8-8a8 8 0 0 1 8 8m44 0a8 8 0 1 1-8-8a8 8 0 0 1 8 8" />
              </svg>
              Sessions
            </span>
            <button className="td-new-session-btn" onClick={openNewSessionModal}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              New
            </button>
          </div>

          <div className="td-date-strip">
            {dateChips.map((chip) => (
              <div
                key={chip.key}
                className={`td-date-chip ${selectedDateKey === chip.key ? 'td-date-chip-today' : ''}`}
                onClick={() => setSelectedDateKey(chip.key)}
              >
                <span className="td-day-label">{chip.label}</span>
                <span className="td-day-num">{chip.num}</span>
              </div>
            ))}
          </div>

          <div className="td-session-list">
            {currentSessions.length === 0 ? (
              <div className="td-empty-state">
                <div className="td-empty-state-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <div className="td-empty-state-text">No sessions<br/>scheduled yet!</div>
              </div>
            ) : (
              currentSessions.map((session, index) => {
                const remaining = formatRemainingTime(session.start_time, session.end_time);
                const colorClass = index % 2 === 0 ? 'td-card-lime' : 'td-card-pink';
                const cardId = session._id || index;
                const isRevealed = revealedSessionId === cardId;

                if (isRevealed) {
                  return (
                    <div key={cardId} className="td-session-card td-card-revealed">
                      <div className="td-revealed-details">
                        <div className="td-revealed-row"><strong>Subject:</strong> {session.subject_name || 'General'}</div>
                        <div className="td-revealed-row"><strong>Topic:</strong> {session.topic || 'Untitled Session'}</div>
                        <div className="td-revealed-row"><strong>Description:</strong> {session.description || 'No description'}</div>
                        <div className="td-revealed-row"><strong>Start:</strong> {new Date(session.start_time).toLocaleString()}</div>
                        <div className="td-revealed-row"><strong>End:</strong> {new Date(session.end_time).toLocaleString()}</div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={cardId} className={`td-session-card ${colorClass}`}>
                    <div className="td-card-inner">
                      <span className="td-sc-subject">{session.subject_name || 'General'}</span>
                      <span className="td-sc-topic">{session.topic || 'Untitled Session'}</span>
                      <div className="td-sc-time-row">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                          <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2">
                            <path d="M10 2h4m-2 12l3-3"/><circle cx="12" cy="14" r="8"/>
                          </g>
                        </svg>
                        {remaining || 'Ended'}
                      </div>
                    </div>
                    <div className="td-card-footer">
                      <span className="td-view-details" onClick={() => handleViewDetails(session, index)}>View Details</span>
                      <button
                        className="td-sc-link-btn"
                        onClick={() => window.open(session.class_link || '#', '_blank')}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                          <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                        </svg>
                        Join Class
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* ── Main Content ── */}
        <div className="td-main-content">
          <div className="td-top-bar">
            <svg
              className="td-bell-icon"
              onClick={handleBellClick}
              style={{ color: unreadCount > 0 ? '#32cd32' : 'currentColor' }}
              xmlns="http://www.w3.org/2000/svg"
              width="1em"
              height="1em"
              viewBox="0 0 32 32"
            >
              <path d="M0 0h32v32H0z" fill="none"/><path fill="currentColor" d="M6.5 5h19A4.5 4.5 0 0 1 30 9.5v13a4.5 4.5 0 0 1-4.5 4.5h-19A4.5 4.5 0 0 1 2 22.5v-13A4.5 4.5 0 0 1 6.5 5M29 10.206V9.5A3.5 3.5 0 0 0 25.5 6h-19A3.5 3.5 0 0 0 3 9.5v.706l13 7.222zM3 11.35V22.5A3.5 3.5 0 0 0 6.5 26h19a3.5 3.5 0 0 0 3.5-3.5V11.35l-12.757 7.087a.5.5 0 0 1-.486 0z"/>
            </svg>
          </div>

          <div className="td-welc-name">{getGreeting()}, {user.firstName} {user.lastName}</div>

          <div className="td-group-items">
            <div className="td-main-groups">
              <div className="td-main-groups-header">Main Groups</div>
              <div className="td-main-groups-items">
                {subjects.slice(0, 3).map((sub, index) => {
                  const onlineCount = subjectPresence[sub.subjectId] || 0;
                  const isActive = onlineCount > 0;
                  const groupClass = `td-subject${index + 1}`;
                  return (
                    <div key={sub.subjectId} className={groupClass} onClick={() => navigate(`/portal/${sub.subjectId}`)}>
                      <div className="td-subject-row">
                        <div className="td-subject-info">
                          <span className="td-mgsn">{sub.subjectDisplayName}</span>
                          <span className="td-active-status">
                            <span className={isActive ? 'td-green-dot' : 'td-red-dot'}></span>
                            {isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="td-subject-count">{sub.tuteeCount || 0}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {subjects.length === 0 && (
                <div className="td-main-groups-empty">
                  <div className="td-main-groups-empty-text">No subjects selected yet.<br/>Manage groups to add one.</div>
                </div>
              )}
              <div className="td-groups-footer" onClick={goToSubjects}>
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 20 20">
                  <path d="M0 0h20v20H0z" fill="none"/><g fill="currentColor" fillRule="evenodd" clipRule="evenodd"><path d="M5 9a2 2 0 1 0 0-4a2 2 0 0 0 0 4m0 1a3 3 0 1 0 0-6a3 3 0 0 0 0 6"/><path d="M3.854 8.896a.5.5 0 0 1 0 .708l-.338.337A3.47 3.47 0 0 0 2.5 12.394v1.856a.5.5 0 1 1-1 0v-1.856a4.47 4.47 0 0 1 1.309-3.16l.337-.338a.5.5 0 0 1 .708 0m11.792-.3a.5.5 0 0 0 0 .708l.338.337A3.47 3.47 0 0 1 17 12.094v2.156a.5.5 0 0 0 1 0v-2.156a4.47 4.47 0 0 0-1.309-3.16l-.337-.338a.5.5 0 0 0-.708 0"/><path d="M14 9a2 2 0 1 1 0-4a2 2 0 0 1 0 4m0 1a3 3 0 1 1 0-6a3 3 0 0 1 0 6m-4.5 3.25a2.5 2.5 0 0 0-2.5 2.5v1.3a.5.5 0 0 1-1 0v-1.3a3.5 3.5 0 0 1 7 0v1.3a.5.5 0 1 1-1 0v-1.3a2.5 2.5 0 0 0-2.5-2.5"/><path d="M9.5 11.75a2 2 0 1 0 0-4a2 2 0 0 0 0 4m0 1a3 3 0 1 0 0-6a3 3 0 0 0 0 6"/></g>
                </svg>
                Manage Groups
              </div>
            </div>

            <div className="td-dash-private-requests">
              <div className="td-dash-private-requets-header">All Requests</div>
              <div className="td-dash-private-requests-empty">
                <div className="td-dash-private-requests-empty-text">Click manage requests to see all requests.</div>
              </div>
              <div className="td-requets-footer" onClick={goToRequests}>
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 20 20">
                  <path d="M0 0h20v20H0z" fill="none"/><g fill="none" stroke="currentColor" strokeLinecap="round"><circle cx="9.5" cy="5.5" r="3"/><path d="M15 16.5v-2c0-3.098-2.495-6-5.5-6S4 11.402 4 14.5v2"/></g>
                </svg>
                Manage Requests
              </div>
            </div>
          </div>

          <div className="td-bottom-boxes">
            <div className="td-wallet-box">
              <div className="td-wallet-box-header">Wallet and Subscriptions</div>
              <div className="td-wallet-graph-wrap" ref={walletGraphRef}>
                <canvas id="tdWalletCanvas"></canvas>
                <div className="td-r-badge" ref={badgeRef}>R</div>
              </div>
              <div className="td-wallet-box-footer" onClick={goToWallet}>
                <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 28 28">
                  <path d="M0 0h28v28H0z" fill="none"/><path fill="currentColor" d="M20.943 8.5h1.958l.226-.268a1.75 1.75 0 0 0-.216-2.466L18.315 1.91a1.75 1.75 0 0 0-2.466.216L10.5 8.5h1.958l4.54-5.41a.25.25 0 0 1 .352-.031l.89.745L14.3 8.5h1.957l3.131-3.731l2.559 2.146a.25.25 0 0 1 .03.352zm-2.193 8a.75.75 0 0 0 0 1.5h2.5a.75.75 0 0 0 0-1.5zM5.5 6h5.79l-1.26 1.5H5.5a1 1 0 0 0 0 2h15.75A3.75 3.75 0 0 1 25 13.25v8A3.75 3.75 0 0 1 21.25 25H6.75A3.75 3.75 0 0 1 3 21.25V8.5A2.5 2.5 0 0 1 5.5 6m-1 4.792V21.25a2.25 2.25 0 0 0 2.25 2.25h14.5a2.25 2.25 0 0 0 2.25-2.25v-8A2.25 2.25 0 0 0 21.25 11H5.5c-.356 0-.694-.074-1-.208"/>
                </svg>
                Personal Wallet
              </div>
            </div>

            <div className="td-account-box">
              <div className="td-account-box-header">Account & Help</div>
              <div className="td-account-box-subheader">Take control of your tutoring account or reach out whenever you need a hand.</div>
              <div className="td-account-items">
                <div className="td-account-item" onClick={goToProfile}>
                  <span className="td-account-item-icon"><svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24"><path d="M0 0h24v24H0z" fill="none"/><path fill="currentColor" d="M14.154 12.462h4.077v-1h-4.077zm0-2.77h4.077v-1h-4.077zm-8.385 5.616h6.616v-.166q0-.875-.88-1.355t-2.428-.48t-2.429.48t-.879 1.355zm4.374-4.05q.434-.433.434-1.066t-.434-1.066t-1.066-.434t-1.066.434t-.434 1.066t.434 1.067t1.066.433t1.066-.433M4.615 19q-.69 0-1.153-.462T3 17.384V6.616q0-.691.463-1.153T4.615 5h14.77q.69 0 1.152.463T21 6.616v10.769q0 .69-.463 1.153T19.385 19zm0-1h14.77q.23 0 .423-.192t.192-.424V6.616q0-.231-.192-.424T19.385 6H4.615q-.23 0-.423.192T4 6.616v10.769q0 .23.192.423t.423.192M4 18V6z"/></svg></span>
                  <span className="td-account-item-label">Profile</span>
                  <span className="td-account-item-arrow">›</span>
                </div>
                <div className="td-account-item" onClick={goToSupport}>
                  <span className="td-account-item-icon"><svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24"><path d="M0 0h24v24H0z" fill="none"/><path fill="currentColor" d="M11.996 16.212q.333 0 .56-.228q.229-.228.229-.561q0-.332-.228-.56t-.56-.228t-.561.228t-.228.56t.228.56q.228.229.56.229m-.515-3.312h.965q.02-.329.067-.571q.047-.243.172-.479t.322-.474t.49-.53q.72-.721 1.044-1.25q.325-.53.325-1.204q0-1.17-.804-1.887q-.804-.716-1.964-.716q-1.067 0-1.838.569q-.77.569-1.125 1.365l.919.38q.252-.597.719-.99q.468-.394 1.264-.394q.944 0 1.404.517q.459.516.459 1.173q0 .502-.275.92q-.275.417-.771.877q-.367.35-.63.668t-.437.636q-.176.317-.24.653q-.066.335-.066.737M12 21.577L9.423 19H5.615q-.69 0-1.153-.462T4 17.384V4.616q0-.691.463-1.153T5.616 3h12.769q.69 0 1.153.463T20 4.616v12.769q0 .69-.462 1.153T18.384 19h-3.807zM5.616 18h4.223L12 20.162L14.162 18h4.223q.269 0 .442-.173t.173-.442V4.615q0-.269-.173-.442T18.385 4H5.615q-.269 0-.442.173T5 4.616v12.769q0 .269.173.442t.443.173M12 11"/></svg></span>
                  <span className="td-account-item-label">Support</span>
                  <span className="td-account-item-arrow">›</span>
                </div>
              </div>
            </div>
          </div>

          <svg className="td-back-arrow-icon" onClick={handleLogoutClick} xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 512 512">
            <path d="M0 0h512v512H0z" fill="none"/><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" d="m112 352l-64-64l64-64"/><path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="32" d="M64 288h294c58.76 0 106-49.33 106-108v-20"/>
          </svg>
        </div>
      </div>

      {/* ── Notifications Modal ── */}
      {showNotifModal && (
        <div className="td-modal-backdrop td-modal-open" onClick={() => setShowNotifModal(false)}>
          <div className="td-modal" style={{ width: '750px' }} onClick={e => e.stopPropagation()}>
            <button className="td-modal-close" onClick={() => setShowNotifModal(false)}>✕</button>
            <div className="td-modal-title">Notifications</div>
            <div className="td-modal-sub">Stay updated with your latest sessions, requests, and payments.</div>
            <div className="td-modal-body-content">
              {notifications.length === 0 ? (
                <div className="td-notif-empty-state">
                  <div className="td-notif-empty-text">No notifications</div>
                </div>
              ) : (
                notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`td-notif-item ${!notif.read ? 'td-notif-unread' : ''}`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <div className="td-notif-info">
                      <div className="td-notif-title">{notif.title}</div>
                      <div className="td-notif-sub">{notif.subtitle}</div>
                    </div>
                    <div className="td-notif-time">{notif.timeAgo}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Logout Modal ── */}
      {showLogoutModal && (
        <div className="td-modal-backdrop td-modal-open" onClick={handleLogoutCancel}>
          <div className="td-modal" style={{ width: '420px' }} onClick={e => e.stopPropagation()}>
            <button className="td-modal-close" onClick={handleLogoutCancel}>✕</button>
            <div className="td-logout-modal-content">
              <div className="td-logout-header-row">
               
                <div className="td-logout-header-text">
                  <div className="td-modal-title">Confirm Logout</div>
                  <div className="td-modal-sub">Are you sure you want to log out of your account?</div>
                </div>
              </div>
              <div className="td-modal-actions">
                <button className="td-btn-cancel" onClick={handleLogoutCancel}>Cancel</button>
                <button className="td-btn-confirm-red" onClick={handleLogoutConfirm}>Yes, log out</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── New Session Modal ── */}
      {showNewSessionModal && (
        <div className="td-ns-modal-backdrop td-ns-modal-open" onClick={closeNewSessionModal}>
          <div className="td-ns-modal" onClick={e => e.stopPropagation()}>
            <button className="td-ns-modal-close" onClick={closeNewSessionModal}>✕</button>
            <div className="td-ns-modal-icon-ring">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </div>
            <div className="td-ns-modal-title">New Session</div>
            <div className="td-ns-modal-sub">Create a new tutoring session for one of your subjects.</div>
            <form onSubmit={createSession}>
              <div className="td-ns-modal-field">
                <label className="td-ns-modal-label">Subject</label>
                <select className="td-ns-modal-select" id="subject" value={sessionForm.subject} onChange={handleSessionFormChange} required>
                  <option value="">Select a subject...</option>
                  {sessionSubjectOptions.map(sub => (
                    <option key={sub.subjectId} value={sub.subjectId}>{sub.subjectDisplayName}</option>
                  ))}
                </select>
              </div>
              <div className="td-ns-modal-field">
                <label className="td-ns-modal-label">Topic</label>
                <input type="text" className="td-ns-modal-input" id="topic" placeholder="e.g., Differentiation Rules" maxLength="80" value={sessionForm.topic} onChange={handleSessionFormChange} required />
              </div>
              <div className="td-ns-modal-field">
                <div className="td-ns-modal-label-row">
                  <label className="td-ns-modal-label">Session description</label>
                  <span className="td-ns-modal-char-count">{sessionForm.description.length} / 200</span>
                </div>
                <textarea className="td-ns-modal-textarea" id="description" maxLength="200" placeholder="Describe what will be covered in this session..." value={sessionForm.description} onChange={handleSessionFormChange}></textarea>
              </div>
              <div className="td-ns-datetime-row">
                <div className="td-ns-modal-field">
                  <label className="td-ns-modal-label">Start date & time</label>
                  <input type="datetime-local" className="td-ns-modal-input" id="startDate" value={sessionForm.startDate} onChange={handleSessionFormChange} required />
                </div>
                <div className="td-ns-modal-field">
                  <label className="td-ns-modal-label">End date & time</label>
                  <input type="datetime-local" className="td-ns-modal-input" id="endDate" value={sessionForm.endDate} onChange={handleSessionFormChange} required />
                </div>
              </div>
              <div className="td-ns-modal-field">
                <label className="td-ns-modal-label">Class link</label>
                <input type="text" className="td-ns-modal-input" id="link" placeholder="Paste your class link here..." value={sessionForm.link} onChange={handleSessionFormChange} />
              </div>
              <div className="td-ns-modal-actions">
                <button type="button" className="td-ns-btn-cancel" onClick={closeNewSessionModal}>Cancel</button>
                <button type="submit" className="td-ns-btn-confirm">Create Session</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TutorDashboard;